import type {
  ScenarioInput,
  ScenarioSummary,
  ScenarioDetail,
  ScenarioCreateResponse,
  ComputeResponse,
  VehicleProfileSuggestion,
  MarketRatesResponse,
  GeographicDefaultsResponse,
  GasPricesResponse,
  FleetTCOResponse,
  LineItem,
  ScenarioComparisonResponse,
} from '../types';
import { supabase } from '../lib/supabase';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';
const BASE_URL = `${API_BASE}/api`;

// Resilience (UX audit P0): the backend runs on a free Render dyno that cold-
// starts after idle, which can take ~50s to wake. A bare fetch() has no
// timeout, so before this every surface that awaited a request would spin
// "Loading…" forever when the dyno was asleep or the network dropped. We give
// every request a bounded deadline and surface a distinguishable timeout error
// so callers can show a "server is waking up — retry" interstitial instead of
// hanging. The deadline is generous enough to ride through a normal cold start.
const DEFAULT_TIMEOUT_MS = 45_000;

/**
 * Thrown when a request exceeds DEFAULT_TIMEOUT_MS (or the caller's override).
 * Callers can branch on `isTimeout` / `isColdStart` to render the waking-up
 * interstitial rather than a generic error. `isColdStart` is also true for
 * connection-level failures (dyno unreachable) so the retry copy still applies.
 */
export class ApiTimeoutError extends Error {
  readonly isTimeout = true;
  readonly isColdStart = true;
  constructor(message = 'The server is taking longer than usual to respond.') {
    super(message);
    this.name = 'ApiTimeoutError';
  }
}

/** True for errors a "server is waking up (~1 min), retry" message should cover. */
export function isColdStartError(err: unknown): boolean {
  if (err instanceof ApiTimeoutError) return true;
  // A network-level failure (dyno asleep / unreachable) surfaces as a TypeError
  // ("Failed to fetch") rather than an HTTP status; treat it the same way.
  return err instanceof TypeError;
}

async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await globalThis.fetch(input, { ...init, signal: controller.signal });
  } catch (e) {
    // An abort fired by our timer surfaces as an AbortError DOMException; map it
    // to the typed timeout so callers don't have to special-case AbortError.
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new ApiTimeoutError();
    }
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`API ${res.status}: ${text}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export async function getAuthHeaders(): Promise<HeadersInit> {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    };
  }
  return { 'Content-Type': 'application/json' };
}

export async function getScenarios(): Promise<ScenarioSummary[]> {
  const res = await fetchWithTimeout(`${BASE_URL}/scenarios`, {
    headers: await getAuthHeaders(),
  });
  return handleResponse<ScenarioSummary[]>(res);
}

export async function getScenario(id: string | number): Promise<ScenarioDetail> {
  const res = await fetchWithTimeout(`${BASE_URL}/scenarios/${id}`, {
    headers: await getAuthHeaders(),
  });
  return handleResponse<ScenarioDetail>(res);
}

export async function createScenario(
  scenario: ScenarioInput,
): Promise<ScenarioCreateResponse> {
  const res = await fetchWithTimeout(`${BASE_URL}/scenarios`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(scenario),
  });
  return handleResponse<ScenarioCreateResponse>(res);
}

export async function updateScenario(
  id: string | number,
  scenario: ScenarioInput,
): Promise<ScenarioCreateResponse> {
  const res = await fetchWithTimeout(`${BASE_URL}/scenarios/${id}`, {
    method: 'PUT',
    headers: await getAuthHeaders(),
    body: JSON.stringify(scenario),
  });
  return handleResponse<ScenarioCreateResponse>(res);
}

export async function deleteScenario(id: string | number): Promise<void> {
  const res = await fetchWithTimeout(`${BASE_URL}/scenarios/${id}`, {
    method: 'DELETE',
    headers: await getAuthHeaders(),
  });
  return handleResponse<void>(res);
}

export async function computeScenario(
  scenario: ScenarioInput,
): Promise<ComputeResponse> {
  const res = await fetchWithTimeout(`${BASE_URL}/compute`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(scenario),
  });
  return handleResponse<ComputeResponse>(res);
}

export async function computeFleetTco(
  body:
    | {
        line_items: LineItem[];
        scenario_annual_miles?: number;
        investment_return_rate?: number;
        // Phase 22.7: scenario-level fuel-price defaults piggyback on the
        // line-items request shape so the FleetEditor can supply them.
        scenario_fuel_cost_per_gallon?: number | null;
        scenario_diesel_price_per_gallon?: number | null;
        scenario_electricity_rate_per_kwh?: number | null;
      }
    | {
        scenario: ScenarioInput;
        scenario_annual_miles?: number;
        investment_return_rate?: number;
      },
): Promise<FleetTCOResponse> {
  const res = await fetchWithTimeout(`${BASE_URL}/fleet-tco`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse<FleetTCOResponse>(res);
}

export async function computeScenarioComparison(
  scenario_a: ScenarioInput,
  scenario_b: ScenarioInput,
): Promise<ScenarioComparisonResponse> {
  const res = await fetchWithTimeout(`${BASE_URL}/scenario-comparison`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ scenario_a, scenario_b }),
  });
  return handleResponse<ScenarioComparisonResponse>(res);
}

export async function exportScenarioJson(id: string | number): Promise<Blob> {
  const headers = await getAuthHeaders();
  const res = await fetchWithTimeout(`${BASE_URL}/export/scenario/${id}`, { headers });
  if (!res.ok) throw new Error(`Export error ${res.status}`);
  return res.blob();
}

export async function exportResultsJson(scenario: ScenarioInput): Promise<Blob> {
  const res = await fetchWithTimeout(`${BASE_URL}/export/results`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(scenario),
  });
  if (!res.ok) throw new Error(`Export error ${res.status}`);
  return res.blob();
}

export async function exportResultsCsv(scenario: ScenarioInput): Promise<Blob> {
  const res = await fetchWithTimeout(`${BASE_URL}/export/results/csv`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(scenario),
  });
  if (!res.ok) throw new Error(`Export error ${res.status}`);
  return res.blob();
}

// Phase 26 Part 1: structured CSV export — accepts already-computed result.
export interface ScenarioCsvBody {
  scenario: ScenarioInput;
  mode: 'personal' | 'fleet';
  personal_result?: ComputeResponse | null;
  fleet_result?: FleetTCOResponse | null;
}

export interface ExportedCsv {
  blob: Blob;
  filename: string;
}

function parseFilename(contentDisposition: string | null, fallback: string): string {
  if (!contentDisposition) return fallback;
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return match?.[1] ?? fallback;
}

export async function exportScenarioCsv(body: ScenarioCsvBody): Promise<ExportedCsv> {
  const res = await fetchWithTimeout(`${BASE_URL}/export/scenario-csv`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  return { blob, filename: parseFilename(res.headers.get('content-disposition'), 'vehicletco_scenario.csv') };
}

export interface CompareCsvBody {
  scenario_a: ScenarioInput;
  scenario_b: ScenarioInput;
  comparison: ScenarioComparisonResponse;
  mode?: 'personal' | 'fleet';
}

export async function exportCompareCsv(body: CompareCsvBody): Promise<ExportedCsv> {
  const res = await fetchWithTimeout(`${BASE_URL}/export/compare-csv`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  return { blob, filename: parseFilename(res.headers.get('content-disposition'), 'vehicletco_compare.csv') };
}

// v1.43.0 — PDF exports. Same input shapes as the CSV endpoints so the
// frontend can post a single precomputed result for either format.
export async function exportScenarioPdf(body: ScenarioCsvBody): Promise<ExportedCsv> {
  const res = await fetchWithTimeout(`${BASE_URL}/export/scenario-pdf`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  return { blob, filename: parseFilename(res.headers.get('content-disposition'), 'vehicletco_scenario.pdf') };
}

export async function exportComparePdf(body: CompareCsvBody): Promise<ExportedCsv> {
  const res = await fetchWithTimeout(`${BASE_URL}/export/compare-pdf`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  return { blob, filename: parseFilename(res.headers.get('content-disposition'), 'vehicletco_compare.pdf') };
}

export async function getVehicleProfile(
  vehicleType: string,
  purchasePrice: number,
  horizonMonths: number,
  purchaseCondition: string = 'new',
  modelYear?: number | null,
): Promise<VehicleProfileSuggestion> {
  const params = new URLSearchParams({
    purchase_price: String(purchasePrice),
    horizon_months: String(horizonMonths),
    purchase_condition: purchaseCondition,
  });
  if (modelYear != null) {
    params.set('model_year', String(modelYear));
  }
  const headers = await getAuthHeaders();
  const res = await fetchWithTimeout(`${BASE_URL}/vehicle-profile/${vehicleType}?${params}`, { headers });
  return handleResponse<VehicleProfileSuggestion>(res);
}

export async function getMarketRates(): Promise<MarketRatesResponse> {
  const res = await fetchWithTimeout(`${BASE_URL}/market-rates`, {
    headers: await getAuthHeaders(),
  });
  return handleResponse<MarketRatesResponse>(res);
}

export async function getGeographicDefaults(
  state: string,
  msrp: number = 30000,
  vehicleAge: number = 0,
  isEv: boolean = false,
  isPhev: boolean = false,
): Promise<GeographicDefaultsResponse> {
  const params = new URLSearchParams({
    msrp: String(msrp),
    vehicle_age: String(vehicleAge),
    is_ev: String(isEv),
    is_phev: String(isPhev),
  });
  const res = await fetchWithTimeout(`${BASE_URL}/geographic-defaults/${state}?${params}`, {
    headers: await getAuthHeaders(),
  });
  return handleResponse<GeographicDefaultsResponse>(res);
}

export async function getGasPrices(): Promise<GasPricesResponse> {
  const res = await fetchWithTimeout(`${BASE_URL}/gas-prices`, {
    headers: await getAuthHeaders(),
  });
  return handleResponse<GasPricesResponse>(res);
}

// Phase 24: per-account Personal/Fleet mode persistence.
// Phase 25.5: onboarding-tour completion flags piggyback on the same
// /api/profile endpoint so initial load only needs one request.
export interface ProfileResponse {
  user_id: string;
  email: string;
  app_mode: 'individual' | 'fleet';
  personal_tour_seen: boolean;
  fleet_tour_seen: boolean;
  // Phase 26 Part 2: subscription tier gating fleet features.
  tier: Tier;
  // v1.42.4 Fix 3: user's default mode preference at login.
  default_mode: DefaultMode;
}

export type DefaultMode = 'individual' | 'fleet' | 'ask';

// Phase 26 Part 2: subscription tiers.
export type Tier = 'free' | 'promo' | 'paid';

export interface PromoRedeemResponse {
  tier: Tier;
  message: string;
}

export interface PromoStatusResponse {
  tier: Tier;
  redeemed_codes: string[];
}

export interface ProfilePatch {
  app_mode?: 'individual' | 'fleet';
  personal_tour_seen?: boolean;
  fleet_tour_seen?: boolean;
  default_mode?: DefaultMode;
}

export async function getProfile(): Promise<ProfileResponse> {
  const res = await fetchWithTimeout(`${BASE_URL}/profile`, {
    headers: await getAuthHeaders(),
  });
  return handleResponse<ProfileResponse>(res);
}

export async function patchProfile(
  patch: ProfilePatch | 'individual' | 'fleet',
): Promise<ProfileResponse> {
  // Back-compat: existing callers pass a plain mode string. New callers pass
  // a partial-update object so any of {app_mode, personal_tour_seen,
  // fleet_tour_seen} can be written in a single round-trip.
  const body: ProfilePatch =
    typeof patch === 'string' ? { app_mode: patch } : patch;
  const res = await fetchWithTimeout(`${BASE_URL}/profile`, {
    method: 'PATCH',
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse<ProfileResponse>(res);
}

// Phase 26 Part 2: promo code redemption + tier status.
export async function redeemPromoCode(code: string): Promise<PromoRedeemResponse> {
  const res = await fetchWithTimeout(`${BASE_URL}/promo/redeem`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ code }),
  });
  return handleResponse<PromoRedeemResponse>(res);
}

export async function getPromoStatus(): Promise<PromoStatusResponse> {
  const res = await fetchWithTimeout(`${BASE_URL}/promo/status`, {
    headers: await getAuthHeaders(),
  });
  return handleResponse<PromoStatusResponse>(res);
}

export async function getScenariosByMode(
  mode: 'individual' | 'fleet',
): Promise<ScenarioSummary[]> {
  const res = await fetchWithTimeout(
    `${BASE_URL}/scenarios?mode=${encodeURIComponent(mode)}`,
    { headers: await getAuthHeaders() },
  );
  return handleResponse<ScenarioSummary[]>(res);
}

// ── Phase 26.2 — admin promo code management (gated server-side on
// the project owner's user id; non-admins receive 404 here as well). ──

export interface AdminPromoCode {
  id: string;
  code: string;
  tier_granted: 'promo' | 'paid';
  max_uses: number;
  use_count: number;
  expires_at: string | null;
  notes: string | null;
  created_at: string | null;
  active: boolean;
}

export interface AdminPromoCodeCreate {
  code: string;
  tier_granted: 'promo' | 'paid';
  max_uses: number;
  expires_at?: string | null;
  notes?: string | null;
}

export async function listAdminPromoCodes(): Promise<AdminPromoCode[]> {
  const res = await fetchWithTimeout(`${BASE_URL}/admin/promo-codes`, {
    headers: await getAuthHeaders(),
  });
  return handleResponse<AdminPromoCode[]>(res);
}

export async function createAdminPromoCode(
  payload: AdminPromoCodeCreate,
): Promise<AdminPromoCode> {
  const res = await fetchWithTimeout(`${BASE_URL}/admin/promo-codes`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse<AdminPromoCode>(res);
}

export async function deactivateAdminPromoCode(
  id: string,
): Promise<AdminPromoCode> {
  const res = await fetchWithTimeout(`${BASE_URL}/admin/promo-codes/${id}/deactivate`, {
    method: 'PATCH',
    headers: await getAuthHeaders(),
  });
  return handleResponse<AdminPromoCode>(res);
}
